import { HOP, SPEED, TOUCH_SPEED } from '@accra/shared';
import type { Actions } from '../core/input/actions';
import type { BusState } from '../game/RunState';
import { clampLane, isSettledIn, laneX } from './lanes';
import { STEER, STEER_SHARP, headingOf, steerToward, withGrip } from './steering';

export interface DrivingAssist {
  /** Highest speed the stop assist allows right now (Infinity when inactive). */
  maxSpeed: number;
}

/** Several things can limit your speed at once (a stop, a pump, a checkpoint): the slowest wins. */
export function slowest(...assists: DrivingAssist[]): DrivingAssist {
  return { maxSpeed: Math.min(...assists.map((a) => a.maxSpeed)) };
}

/** How much a slope changes things: uphill the engine labours and tops out lower; downhill it pulls away. */
export const GRADE = { topSpeed: 2.5, pull: 8, minPull: 0.35, burn: 6, minBurn: 0.6 } as const;

function targetSpeed(bus: BusState, actions: Actions, assist: DrivingAssist): number {
  if (actions.brake || bus.engineOff) return 0;
  const wanted = (actions.gas ? bus.cruise * SPEED.gasMultiplier : bus.cruise) * bus.speedBoost * (1 - bus.grade * GRADE.topSpeed);
  return Math.min(wanted, assist.maxSpeed);
}

function updateSpeed(bus: BusState, actions: Actions, assist: DrivingAssist, dt: number): void {
  const target = targetSpeed(bus, actions, assist);
  const accel = bus.touchDriving ? TOUCH_SPEED.accel : SPEED.accel;
  const ramp = bus.touchDriving ? TOUCH_SPEED.rampPerSec : SPEED.rampPerSec;
  if (bus.speed < target) {
    bus.speed = Math.min(target, bus.speed + accel * bus.accelFactor * Math.max(GRADE.minPull, 1 - bus.grade * GRADE.pull) * dt);
  } else {
    const decel = actions.brake || (assist.maxSpeed < bus.speed && !bus.engineOff) ? SPEED.brakeDecel * bus.grip : SPEED.coastDecel;
    bus.speed = Math.max(target, bus.speed - decel * dt);
  }
  if (bus.speed > 1) bus.cruise = Math.min(SPEED.max, bus.cruise + ramp * dt);
}

function updateLane(bus: BusState, actions: Actions, dt: number): void {
  if (actions.laneLeft) bus.targetLane = clampLane(bus.targetLane - 1);
  if (actions.laneRight) bus.targetLane = clampLane(bus.targetLane + 1);
  const goal = laneX(bus.targetLane);
  bus.lateralAccel = steerToward(bus, goal, bus.speed, dt, withGrip(bus.touchDriving ? STEER_SHARP : STEER, bus.grip));
  bus.yaw = headingOf(bus.vx, bus.speed);
  if (bus.x === goal || isSettledIn(bus.x, bus.targetLane, 0.15)) bus.lane = bus.targetLane;
}

function updateHop(bus: BusState, actions: Actions, dt: number): void {
  const grounded = bus.y <= 0;
  if (actions.hop && grounded) bus.vy = HOP.velocity * bus.hopBoost;
  if (!grounded || bus.vy > 0) {
    bus.vy -= HOP.gravity * dt;
    bus.y = Math.max(0, bus.y + bus.vy * dt);
    if (bus.y === 0) bus.vy = 0;
  }
}

/** Advances the bus one tick. Returns metres travelled this tick. */
export function updateDriving(bus: BusState, actions: Actions, assist: DrivingAssist, dt: number): number {
  updateSpeed(bus, actions, assist, dt);
  updateLane(bus, actions, dt);
  updateHop(bus, actions, dt);
  bus.stumbleWindow = Math.max(0, bus.stumbleWindow - dt);
  const travelled = bus.speed * dt;
  bus.s += travelled;
  return travelled;
}

export function isFast(bus: BusState): boolean {
  return bus.speed > bus.cruise * SPEED.fastThreshold;
}
